import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CreditCard, ArrowRight } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function Billing() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/billing/me');
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCancel = async () => {
    if (!window.confirm(t('billing.cancelConfirm'))) return;
    try {
      await api.post('/billing/cancel');
      toast.success(t('billing.cancelDone'));
      await refresh();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('auth.errors.generic'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="main-content">
        <h1 className="text-3xl font-bold text-slate-900 mb-6">{t('billing.title')}</h1>

        {loading ? <p className="text-slate-500">{t('common.loading')}</p> : (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">{t('billing.currentPlan')}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{data?.plan_name || data?.plan_id}</p>
                  {data?.plan_id === 'free_trial' && data?.trial_end_date && (
                    <p className="text-sm text-amber-700 mt-1">
                      {t('billing.trialEnds', { date: new Date(data.trial_end_date).toLocaleDateString() })}
                    </p>
                  )}
                  {data?.plan_id !== 'free_trial' && data?.stripe_subscription_id && (
                    <p className="text-sm text-slate-600 mt-1">
                      Stripe sub: <span className="font-mono text-xs">{data.stripe_subscription_id}</span>
                    </p>
                  )}
                  <p className="text-sm text-slate-700 mt-2">
                    {Math.round(data?.credit_balance || 0)} {t('credits.balance').toLowerCase()}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  {data?.plan_id === 'free_trial' ? (
                    <Link to="/pricing">
                      <Button>
                        {t('billing.upgrade')} <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="outline" onClick={onCancel}>
                      {t('billing.cancelSubscription')}
                    </Button>
                  )}
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="font-semibold mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                {t('billing.transactions')}
              </h2>
              {(!data?.transactions || data.transactions.length === 0) ? (
                <p className="text-slate-500 text-sm">{t('billing.noTransactions')}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-slate-600 text-left">
                    <tr>
                      <th className="py-2">{t('billing.date')}</th>
                      <th className="py-2">{t('billing.status')}</th>
                      <th className="py-2 text-right">{t('billing.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((tx) => (
                      <tr key={tx.id} className="border-t">
                        <td className="py-2 text-xs">{new Date(tx.created_at).toLocaleString()}</td>
                        <td className="py-2 capitalize">{tx.status}</td>
                        <td className="py-2 text-right">${Number(tx.amount || 0).toFixed(2)} {tx.currency?.toUpperCase()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
